import type {
  BackupPolicySecurityConfig,
  Condition,
  Configuration,
  DetectorType,
  FindingExclusion,
  FindingExclusionFilterConditions,
  FindingObjectType,
  ListFindingExclusionsResponse,
  SecurityFindingsFilterConditions,
  SecurityScanDetectorTypeEnum,
} from '@repo/api-gw-sdk';
import {
  FindingExclusionScope,
  FindingExclusionsApi,
  GetSecurityPlanApi,
  IsSecurityPlanEnabledApi,
  ListExclusionPatternResourcesApi,
  SaveSecurityPlanApi,
  SecurityScansApi,
  UpdateSecurityScanPoliciesConfigApi,
} from '@repo/api-gw-sdk';

import { ignoreNotFound } from '@/components/security/findingExclusionErrors';
import {
  toFindingExclusionDetector,
  toFindingExclusionFilters,
} from '@/components/security/findingExclusionFilters';
import { convertQuery } from '@/utils/conditionQuery';
import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const baseUrl = '/security';

export const security = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const securityScansApi = new SecurityScansApi(config);
  const getSecurityPlanApi = new GetSecurityPlanApi(config);
  const isSecurityPlanEnabledApi = new IsSecurityPlanEnabledApi(config);
  const saveSecurityPlanApi = new SaveSecurityPlanApi(config);
  const updateSecurityScanPoliciesConfigApi =
    new UpdateSecurityScanPoliciesConfigApi(config);
  const findingExclusionsApi = new FindingExclusionsApi(config);
  const listExclusionPatternResourcesApi = new ListExclusionPatternResourcesApi(
    config
  );

  // A provider resource ID scopes the exclusion to that resource; without one it applies account-wide.
  const toFindingExclusionTarget = (providerResourceId: string | undefined) =>
    providerResourceId
      ? {
          scope: FindingExclusionScope.FINDING_EXCLUSION_SCOPE_RESOURCE,
          providerResourceId,
        }
      : { scope: FindingExclusionScope.FINDING_EXCLUSION_SCOPE_ACCOUNT };

  // Null filters match no exclusion, which the API cannot be asked for.
  const listFindingExclusions = (
    projectId: string,
    pageSize: number,
    pageToken: string | undefined,
    filters: FindingExclusionFilterConditions | null
  ): Promise<ListFindingExclusionsResponse> =>
    filters === null
      ? Promise.resolve({ findingExclusions: [], totalCount: 0 })
      : findingExclusionsApi.listFindingExclusions(
          projectId,
          pageToken,
          pageSize,
          { filters }
        );

  return {
    useScansSummary: (
      resourceId: string,
      detectorType?: SecurityScanDetectorTypeEnum
    ) =>
      httpClient.execute(
        securityScansApi.getSecurityScansSummary.bind(securityScansApi),
        baseUrl,
        'getScansSummary',
        currentProjectId,
        resourceId,
        detectorType
      ),
    excludeResource: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: true,
        }
      ),
    cancelResourceExclusion: (resourceId: string) =>
      securityScansApi.updateResourceSecurityScansExclusion(
        currentProjectId,
        resourceId,
        {
          excluded: false,
        }
      ),
    updatePolicies: (policies: BackupPolicySecurityConfig[]) =>
      updateSecurityScanPoliciesConfigApi.updateSecurityScanPoliciesConfig(
        currentProjectId,
        { policiesConfig: policies }
      ),
    useIsSecurityPlanEnabled: () =>
      httpClient.execute(
        isSecurityPlanEnabledApi.isSecurityPlanEnabled.bind(
          isSecurityPlanEnabledApi
        ),
        baseUrl,
        'isSecurityPlanEnabled',
        currentProjectId
      ),
    useSecurityPlan: () =>
      httpClient.execute(
        getSecurityPlanApi.getSecurityPlan.bind(getSecurityPlanApi),
        baseUrl,
        'getSecurityPlan',
        currentProjectId
      ),
    saveSecurityPlan: (
      condition: Condition | undefined,
      include: string[] = [],
      exclude: string[] = []
    ) =>
      saveSecurityPlanApi.saveSecurityPlan(currentProjectId, {
        condition,
        include,
        exclude,
      }),
    infectedSnapshots: {
      useList: (resourceId: string | undefined) =>
        httpClient.execute(
          securityScansApi.listInfectedSnapshots.bind(securityScansApi),
          resourceId ? baseUrl : null,
          'infectedSnapshots',
          currentProjectId,
          resourceId ?? ''
        ),
    },
    findingCounts: {
      useGet: (resourceId: string | undefined) =>
        httpClient.execute(
          securityScansApi.getResourceFindingCounts.bind(securityScansApi),
          resourceId ? baseUrl : null,
          'resourceFindingCounts',
          currentProjectId,
          resourceId ?? ''
        ),
    },
    findings: {
      useList: (params: {
        enabled?: boolean;
        condition?: Condition;
        from?: string; // ISO date – include snapshots on/after this date
        to?: string; // ISO date – include snapshots on/before this date
        pageIndex?: number;
        pageSize?: number;
      }) => {
        const from = params.from ? dayjs.utc(params.from).toDate() : undefined;
        const to = params.to ? dayjs.utc(params.to).toDate() : undefined;
        const filters: SecurityFindingsFilterConditions = {
          ...convertQuery<SecurityFindingsFilterConditions>(params.condition),
          ...(from || to
            ? {
                snapshotTime: {
                  ...(from ? { _from: from } : {}),
                  ...(to ? { to } : {}),
                },
              }
            : {}),
        };

        const pageSize = params.pageSize ?? 50;
        const pageIndex = params.pageIndex ?? 0;
        const pageToken = pageIndex > 0 ? btoa(`page|${pageIndex}`) : undefined;

        return httpClient.execute(
          securityScansApi.listSecurityFindings.bind(securityScansApi),
          params.enabled === false ? null : baseUrl,
          'listFindings',
          currentProjectId,
          pageSize,
          pageToken,
          { filters }
        );
      },
      // All findings of a single resource, for aggregating them by path in the
      // caller (e.g. affected entities of a listing) with one request instead of
      // one per row. The API caps pageSize at 200, so a resource with more
      // findings than that yields a partial view - compare findings.length with
      // totalCount to detect it.
      useListByResource: (params: {
        resourceId: string;
        snapshotId?: string;
        pageSize?: number;
        enabled?: boolean;
      }) => {
        const filters: SecurityFindingsFilterConditions = {
          resourceId: { _in: [params.resourceId] },
          ...(params.snapshotId
            ? { snapshotId: { _in: [params.snapshotId] } }
            : {}),
        };

        return httpClient.execute(
          securityScansApi.listSecurityFindings.bind(securityScansApi),
          params.enabled === false || !params.resourceId ? null : baseUrl,
          `listFindingsByResource|${params.resourceId}|${params.snapshotId ?? ''}`,
          currentProjectId,
          params.pageSize ?? 200,
          undefined,
          { filters }
        );
      },
    },
    pathExclusion: {
      usePathExclusion: ({
        condition,
        pageIndex,
        pageSize = 50,
      }: {
        condition?: Condition;
        pageIndex: number;
        pageSize?: number;
      }) => {
        const pageToken = pageIndex > 0 ? btoa(`page|${pageIndex}`) : undefined;

        return httpClient.execute(
          listFindingExclusions,
          baseUrl,
          'listFindingExclusions',
          currentProjectId,
          pageSize,
          pageToken,
          toFindingExclusionFilters(condition)
        );
      },
      useExclusionPatternResources: () => {
        return httpClient.execute(
          listExclusionPatternResourcesApi.listExclusionPatternResources.bind(
            listExclusionPatternResourcesApi
          ),
          baseUrl,
          'getExclusionPatternResources',
          currentProjectId
        );
      },
      create: (
        value: string,
        type: FindingObjectType,
        detector: DetectorType,
        providerResourceId?: string
      ) => {
        const findingExclusionDetector = toFindingExclusionDetector(detector);
        if (!findingExclusionDetector) {
          return Promise.reject(
            new Error(`${detector} findings cannot be excluded`)
          );
        }
        return findingExclusionsApi.createFindingExclusion(currentProjectId, {
          ...toFindingExclusionTarget(providerResourceId),
          value,
          type,
          detector: findingExclusionDetector,
        });
      },
      update: (
        { id, value, type, detector }: FindingExclusion,
        providerResourceId?: string
      ) => {
        return findingExclusionsApi.updateFindingExclusion(
          currentProjectId,
          id,
          {
            ...toFindingExclusionTarget(providerResourceId),
            value,
            type,
            detector,
          }
        );
      },
      delete: (id: string) => {
        return findingExclusionsApi
          .deleteFindingExclusion(currentProjectId, id)
          .catch(ignoreNotFound);
      },
    },
  };
};
